import {
  INTERNAL_SERVER_ERROR,
  PAYMENT_ERROR,
  PAYMENT_PROVIDER_MISMATCH,
  PaymentErrorCategory,
  PaymentErrorParams,
  PaymentProvider,
} from "@greenflagdate/shared";
import { clientAdapters } from "./index";
import { GENERIC_PAYMENT_ERROR as GENERIC } from "./types";

/**
 * Copy for a payment failure, when the provider has nothing more specific.
 * The provider's own code map is always preferred — it is far more precise
 * than a category — so these are a floor, not the usual answer.
 */
const CATEGORY_COPY: Record<PaymentErrorCategory, string> = {
  [PaymentErrorCategory.CardDeclined]:
    "Your card was declined. Please try a different card.",
  [PaymentErrorCategory.CardInvalid]:
    "Those card details don't look right. Please double check them.",
  [PaymentErrorCategory.CardExpired]:
    "That card has expired. Please try a different card.",
  [PaymentErrorCategory.Blocked]:
    "Your card was declined. Please check the billing zip code and security code.",
  [PaymentErrorCategory.NotFound]:
    "We couldn't find that payment method. Please re-enter your card.",
  [PaymentErrorCategory.Duplicate]:
    "That card is already saved to your account.",
  [PaymentErrorCategory.AlreadyRefunded]: "That payment was already refunded.",
  [PaymentErrorCategory.Disputed]:
    "That payment is being disputed, so it can't be refunded right now.",
  [PaymentErrorCategory.Configuration]:
    "Something went wrong on our end. Please contact us and we will resolve this issue ASAP.",
  [PaymentErrorCategory.Unavailable]:
    "The payment processor is temporarily unavailable. Please try again in a moment.",
  [PaymentErrorCategory.Unknown]: GENERIC,
};

/**
 * Is this wire code one `getPaymentErrorMessage` has copy for?
 *
 * Callers ask this rather than testing the codes themselves — that is how a
 * third payment code stays a one-line change here instead of a hunt through
 * every error translator.
 */
export const isPaymentErrorCode = (code?: string): boolean =>
  code === PAYMENT_ERROR || code === PAYMENT_PROVIDER_MISMATCH;

/**
 * An error's `params` as they actually arrive: a JSON blob off the wire. The
 * API builds them from `PaymentErrorParams`, but nothing between here and
 * there checks that, so they are read rather than asserted.
 */
export type PaymentErrorEnvelope = Record<string, unknown>;

const asMember = <T extends string>(
  values: readonly T[],
  value: unknown,
): T | undefined => (values.includes(value as T) ? (value as T) : undefined);

/**
 * Pull the fields we understand out of the envelope, dropping anything we do
 * not recognize.
 *
 * A cast would be shorter and wrong: a provider string with no adapter, or a
 * category invented by a newer API, would index straight into a lookup and
 * produce `undefined` where copy was expected. Narrowing here means an
 * unrecognized value falls through to the next-best copy instead.
 */
const readParams = (
  params?: PaymentErrorEnvelope,
): Partial<PaymentErrorParams> => ({
  provider: asMember(Object.values(PaymentProvider), params?.provider),
  category: asMember(Object.values(PaymentErrorCategory), params?.category),
  code: typeof params?.code === "string" ? params.code : undefined,
});

export const getPaymentErrorMessage = (
  code?: string,
  params?: PaymentErrorEnvelope,
): string => {
  if (code === PAYMENT_PROVIDER_MISMATCH) {
    return "Please update the app to continue with this payment.";
  }
  // A failure that was our fault arrives carrying nothing — no category, no
  // provider, no code — because a 500 body is public. The copy is the same one
  // the Configuration category resolves to; the status is what tells us to use
  // it, and there is nothing else here to be more specific with.
  if (code === INTERNAL_SERVER_ERROR) {
    return CATEGORY_COPY[PaymentErrorCategory.Configuration];
  }
  if (code !== PAYMENT_ERROR) {
    return GENERIC;
  }

  const { provider, category, code: providerCode } = readParams(params);

  return (
    // A pre-adapter API sends failures with no provider stamp, and everything
    // it could have been talking to was Authorize.net.
    clientAdapters[provider ?? PaymentProvider.AuthorizeNet]?.translateError(
      providerCode,
    ) ??
    (category ? CATEGORY_COPY[category] : undefined) ??
    GENERIC
  );
};
